/** Setup progress in words (#2833): five steps, "3 of 5", the next step and the wait. */
import type { AdminSetupStep } from "../../../api/useAdminSetup";
import { calendarDaysBetween, dayWords } from "./adminWords";

type StepKey = AdminSetupStep["key"];

export const SETUP_ORDER: readonly StepKey[] = [
  "stations",
  "people",
  "invited",
  "first_solve",
  "first_publish",
];

const STEP_LABEL: Record<StepKey, string> = {
  stations: "Stations",
  people: "People",
  invited: "Invited",
  first_solve: "First solve",
  first_publish: "First publish",
};

const NEXT_WORDS: Record<StepKey, string> = {
  stations: "add stations",
  people: "add people",
  invited: "invite people",
  first_solve: "solve",
  first_publish: "publish",
};

export function stepLabel(key: StepKey): string {
  return STEP_LABEL[key];
}

/** The steps in the fixed order. A step the API left out counts as not done. */
export function orderedSteps(steps: readonly AdminSetupStep[]): AdminSetupStep[] {
  return SETUP_ORDER.map((key) => steps.find((s) => s.key === key) ?? { key, done: false });
}

export function setupDone(steps: readonly AdminSetupStep[]): number {
  return orderedSteps(steps).filter((s) => s.done).length;
}

/** "3 of 5". */
export function setupCount(steps: readonly AdminSetupStep[]): string {
  return `${setupDone(steps)} of ${SETUP_ORDER.length}`;
}

/** The first step not done, or null when all five are done. */
export function firstOpenStep(steps: readonly AdminSetupStep[]): AdminSetupStep | null {
  return orderedSteps(steps).find((s) => !s.done) ?? null;
}

/** Calendar days since the latest done step, or null when no done step has a date. */
export function waitingDays(
  steps: readonly AdminSetupStep[],
  now: Date = new Date(),
): number | null {
  const ordered = orderedSteps(steps);
  if (!ordered.some((s) => !s.done)) return null;
  let latest: Date | null = null;
  for (const s of ordered) {
    if (!s.done || !s.at) continue;
    const t = new Date(s.at);
    if (Number.isNaN(t.getTime())) continue;
    if (!latest || t > latest) latest = t;
  }
  return latest ? calendarDaysBetween(latest, now) : null;
}

function daysWords(n: number): string {
  return `${n} ${n === 1 ? "day" : "days"}`;
}

function publishedAt(steps: readonly AdminSetupStep[]): string | null {
  return dayWords(orderedSteps(steps)[4]?.at);
}

/** The tile line: "Next: invite people", "Next: publish, waiting 5 days" or "Published 24 Oct". */
export function tileSetupWords(steps: readonly AdminSetupStep[], now: Date = new Date()): string {
  const open = firstOpenStep(steps);
  if (!open) {
    const when = publishedAt(steps);
    return when ? `Published ${when}` : "Published";
  }
  const wait = waitingDays(steps, now);
  const waiting = wait !== null && wait >= 1 ? `, waiting ${daysWords(wait)}` : "";
  return `Next: ${NEXT_WORDS[open.key]}${waiting}`;
}

/** The table cell: "No stations yet", "Solved, not published", "Published 24 Oct" or "3 of 5". */
export function tableSetupWords(steps: readonly AdminSetupStep[]): string {
  const o = orderedSteps(steps);
  if (o.every((s) => s.done)) {
    const when = publishedAt(steps);
    return when ? `Published ${when}` : "Published";
  }
  if (!o[0]?.done) return "No stations yet";
  if (o[3]?.done && !o[4]?.done) return "Solved, not published";
  return setupCount(steps);
}

/** The drawer status of one step: "done 3 Nov", "waiting 5 days" or "not yet". */
export function stepStatusWords(
  step: AdminSetupStep,
  steps: readonly AdminSetupStep[],
  now: Date = new Date(),
): string {
  if (step.done) {
    const when = dayWords(step.at);
    return when ? `done ${when}` : "done";
  }
  if (firstOpenStep(steps)?.key === step.key) {
    const wait = waitingDays(steps, now);
    if (wait !== null && wait >= 1) return `waiting ${daysWords(wait)}`;
  }
  return "not yet";
}
